import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import ICU from 'i18next-icu';

import enCommon from './locales/en/common.json';
import enAuth from './locales/en/auth.json';
import enErrors from './locales/en/errors.json';
import enDashboard from './locales/en/dashboard.json';
import enUsers from './locales/en/users.json';
import enModeration from './locales/en/moderation.json';
import enAudit from './locales/en/audit.json';
import enPolicies from './locales/en/policies.json';
import enSettings from './locales/en/settings.json';
import enHomepage from './locales/en/homepage.json';
import enStories from './locales/en/stories.json';
import enAnnouncements from './locales/en/announcements.json';
import enFeatureFlags from './locales/en/featureFlags.json';
import enTranslations from './locales/en/translations.json';
import enDigests from './locales/en/digests.json';
import enNewsletter from './locales/en/newsletter.json';
import enCommerce from './locales/en/commerce.json';
import enPhase2 from './locales/en/phase2.json';
import enCopy from './locales/en/copy.json';

import arCommon from './locales/ar/common.json';
import arAuth from './locales/ar/auth.json';
import arErrors from './locales/ar/errors.json';
import arDashboard from './locales/ar/dashboard.json';
import arUsers from './locales/ar/users.json';
import arModeration from './locales/ar/moderation.json';
import arAudit from './locales/ar/audit.json';
import arPolicies from './locales/ar/policies.json';
import arSettings from './locales/ar/settings.json';
import arHomepage from './locales/ar/homepage.json';
import arStories from './locales/ar/stories.json';
import arAnnouncements from './locales/ar/announcements.json';
import arFeatureFlags from './locales/ar/featureFlags.json';
import arTranslations from './locales/ar/translations.json';
import arDigests from './locales/ar/digests.json';
import arNewsletter from './locales/ar/newsletter.json';
import arCommerce from './locales/ar/commerce.json';
import arPhase2 from './locales/ar/phase2.json';
import arCopy from './locales/ar/copy.json';

import egCommon from './locales/eg-ar/common.json';
import egAuth from './locales/eg-ar/auth.json';
import egErrors from './locales/eg-ar/errors.json';
import egDashboard from './locales/eg-ar/dashboard.json';
import egUsers from './locales/eg-ar/users.json';
import egModeration from './locales/eg-ar/moderation.json';
import egAudit from './locales/eg-ar/audit.json';
import egPolicies from './locales/eg-ar/policies.json';
import egSettings from './locales/eg-ar/settings.json';
import egHomepage from './locales/eg-ar/homepage.json';
import egStories from './locales/eg-ar/stories.json';
import egAnnouncements from './locales/eg-ar/announcements.json';
import egFeatureFlags from './locales/eg-ar/featureFlags.json';
import egTranslations from './locales/eg-ar/translations.json';
import egDigests from './locales/eg-ar/digests.json';
import egNewsletter from './locales/eg-ar/newsletter.json';
import egCommerce from './locales/eg-ar/commerce.json';
import egPhase2 from './locales/eg-ar/phase2.json';
import egCopy from './locales/eg-ar/copy.json';

import { useUiStore, type Locale } from '@/stores/ui';

export const SUPPORTED_LOCALES: readonly Locale[] = ['en', 'ar', 'eg-ar'];
export const DEFAULT_LOCALE: Locale = 'en';
export const NAMESPACES = [
  'common',
  'auth',
  'errors',
  'dashboard',
  'users',
  'moderation',
  'audit',
  'policies',
  'settings',
  'homepage',
  'stories',
  'announcements',
  'featureFlags',
  'translations',
  'digests',
  'newsletter',
  'commerce',
  'phase2',
  'copy',
] as const;
export type Namespace = (typeof NAMESPACES)[number];

const resources = {
  en: {
    common: enCommon,
    auth: enAuth,
    errors: enErrors,
    dashboard: enDashboard,
    users: enUsers,
    moderation: enModeration,
    audit: enAudit,
    policies: enPolicies,
    settings: enSettings,
    homepage: enHomepage,
    stories: enStories,
    announcements: enAnnouncements,
    featureFlags: enFeatureFlags,
    translations: enTranslations,
    digests: enDigests,
    newsletter: enNewsletter,
    commerce: enCommerce,
    phase2: enPhase2,
    copy: enCopy,
  },
  ar: {
    common: arCommon,
    auth: arAuth,
    errors: arErrors,
    dashboard: arDashboard,
    users: arUsers,
    moderation: arModeration,
    audit: arAudit,
    policies: arPolicies,
    settings: arSettings,
    homepage: arHomepage,
    stories: arStories,
    announcements: arAnnouncements,
    featureFlags: arFeatureFlags,
    translations: arTranslations,
    digests: arDigests,
    newsletter: arNewsletter,
    commerce: arCommerce,
    phase2: arPhase2,
    copy: arCopy,
  },
  'eg-ar': {
    common: egCommon,
    auth: egAuth,
    errors: egErrors,
    dashboard: egDashboard,
    users: egUsers,
    moderation: egModeration,
    audit: egAudit,
    policies: egPolicies,
    settings: egSettings,
    homepage: egHomepage,
    stories: egStories,
    announcements: egAnnouncements,
    featureFlags: egFeatureFlags,
    translations: egTranslations,
    digests: egDigests,
    newsletter: egNewsletter,
    commerce: egCommerce,
    phase2: egPhase2,
    copy: egCopy,
  },
} as const;

export async function initI18n(): Promise<typeof i18n> {
  if (i18n.isInitialized) return i18n;
  await i18n
    .use(ICU)
    .use(initReactI18next)
    .init({
      resources,
      lng: useUiStore.getState().locale,
      fallbackLng: { 'eg-ar': ['ar', 'en'], ar: ['en'], default: ['en'] },
      // i18next reads `eg-ar` as language `eg`, region `AR`, and looks it up as
      // `eg-AR` — which matches no resource bundle, so every Egyptian string
      // silently fell through to MSA. Keep codes exactly as they are keyed.
      lowerCaseLng: true,
      ns: [...NAMESPACES],
      defaultNS: 'common',
      interpolation: { escapeValue: false },
      returnNull: false,
    });
  return i18n;
}

/** Subscribe i18next to UI store locale changes. Idempotent. */
export function bindI18nToStore(): () => void {
  return useUiStore.subscribe((state, prev) => {
    if (state.locale !== prev.locale && i18n.isInitialized) {
      void i18n.changeLanguage(state.locale);
    }
  });
}

export default i18n;
